import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import InsightPropTypes from '../models/Insight/InsightPropTypes';

import InsightCard from './insight-cards/insight-card';

import { AnimateIn, Container, Grid, GridItem, TitleSet, media, InternalLink } from '../ui-components';

import { ScrollingCarousel } from '@trendyol-js/react-carousel';

const propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  insights: PropTypes.arrayOf(InsightPropTypes).isRequired,
  label: PropTypes.string,
  title: PropTypes.string,
  layout: PropTypes.string,
  linkShouldAppear: PropTypes.bool,
  link: PropTypes.string
};

/**
 * Renders the Related Insights section
 *
 * @component RelatedInsights
 * 
 * @prop {string} id
 * @prop {string} className
 * @prop {array} insights - list of insights
 * @prop {string} label
 * @prop {string} title - the title of the section
 * @prop {string} layout - default, side-by-side
 * @prop {boolean} linkShouldAppear
 * @prop {string} link
 * 
 * @returns {JSX.Element}
 */
const RelatedInsights = props => {
  const {
    id,
    className,
    insights,
    label = 'Related Insights',
    title,
    layout = 'default',
    linkShouldAppear = false,
    link
  } = props;

  /**
   * Render function that builds JSX that represents the list the first 3 related insights in a row
   *
   * @function renderInsightsListGrid
   * 
   * @returns {JSX.Element}
   */
  const renderInsightsListGrid = () => {
    const insightsList = insights.slice(0, 3).map(insight => {
      return (
        <GridItem key={insight.id} col={4} colxl={6} collg={12}>
          <InsightCard insight={insight} layout={''} />
        </GridItem>
      );
    });

    return <Grid gridGap={24}>{insightsList}</Grid>;
  };

  const renderInsightsList = () => {
    return insights.slice(0, 3).map(insight => {
      return <InsightCard className={'insight-card'} insight={insight} layout={''} />;
    });
  };

  /**
   * Render function that builds JSX that represents the list of the first 3-4 insights in a 1x2 or 1x3 layout
   *
   * @function renderInsightsSideBySide
   * 
   * @returns {JSX.Element}
   */
  const renderInsightsSideBySide = () => {
    return (
      <SideBySideContainer gridGap={24}>
        <LeftColumnContainer length={insights.length} className={className}>
          <InsightCard insight={insights[0]} className={className} headline />
        </LeftColumnContainer>
        <RightColumnContainer length={insights.length} className={className}>
          {insights.slice(1, 4).map(insight =>
            insights.length <= 3 ? (
              <InsightCardWrapper key={insight.id}>
                <InsightCard
                  insight={insight}
                  displayThumbnail={false}
                  displayAuthors={true}
                  layout={layout}
                ></InsightCard>
              </InsightCardWrapper>
            ) : (
              <InsightCardWrapper key={insight.id}>
                <InsightCard
                  insight={insight}
                  displayThumbnail={false}
                  displayAuthors={false}
                  layout={layout}
                ></InsightCard>
              </InsightCardWrapper>
            )
          )}
        </RightColumnContainer>
      </SideBySideContainer>
    );
  };

  /**
   * Helper function that builds JSX of insights based on layout prop
   *
   * @function renderInsightsByLayout
   * 
   * @returns {JSX.Element}
   */
  const renderInsightsByLayout = () => {
    switch (layout) {
      case 'side-by-side':
        return renderInsightsSideBySide();
      default:
        return renderInsightsListGrid();
    }
  };

  const renderInsightsCarousel = () => {
    return (
      <ScrollingCarousel>
        <ScrollingDiv>{renderInsightsList()}</ScrollingDiv>
      </ScrollingCarousel>
    );
  };

  return (
    <>
      <AnimateIn onScroll>
        <RelatedInsightsContainer id={id} className={className} sectionContainer linkShouldAppear={linkShouldAppear}>
          <TitleSet id={'related-insights-title-set'} label={label} title={title} />
          <DesktopWrapper>{renderInsightsByLayout()}</DesktopWrapper>
          <MobileWrapper>{renderInsightsCarousel()}</MobileWrapper>
        </RelatedInsightsContainer>
      </AnimateIn>
      {linkShouldAppear && (
        <MobileViewAllLink withArrow hoverUnderline to={link}>
          {'View All'}
        </MobileViewAllLink>
      )}
    </>
  );
};

RelatedInsights.propTypes = propTypes;

export default RelatedInsights;

const RelatedInsightsContainer = styled(Container)`
  @media screen and (max-width: ${media.largeTop}) {
    gap: 40px;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    padding: 0 0 ${({ linkShouldAppear }) => (linkShouldAppear ? '40px' : '60px')} 0;
  }
`;

const SideBySideContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`;

const LeftColumnContainer = styled(props => <div {...props} />)`
  display: flex;
  max-width: ${({ length }) => (length <= 3 ? '800px' : '600px')};
`;

const RightColumnContainer = styled(props => <div {...props} />)`
  width: 656px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InsightCardWrapper = styled.div``;

const DesktopWrapper = styled.div`
  width: 100%;

  @media screen and (max-width: ${media.mediumTop}) {
    display: none;
  }
`;

const MobileWrapper = styled.div`
  width: 100%;
  @media screen and (width > ${media.mediumTop}) {
    display: none;
  }
`;

const ScrollingDiv = styled.div`
  display: flex;
  gap: 24px;
  padding: 0 20px;

  .insight-card,
  .downloadable-content-card {
    width: 80vw;
    min-width: 350px;
  }
`;

const MobileViewAllLink = styled(InternalLink)`
  display: none;

  @media screen and (max-width: ${media.mediumTop}) {
    display: flex;
    justify-content: center;
    padding-top: 0;
    padding-bottom: 60px;
  }
`;
